import React, { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Target,
  Workflow,
  Sparkles,
  Layers,
  Play,
  Lock,
} from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const SLIDE_THEMES = {
  problem: {
    title: "The Problem",
    icon: Target,
    accent:
      "from-rose-500/[0.10] via-transparent to-amber-500/[0.06]",
    dot: "bg-rose-400",
    glow: "rgba(244,63,94,0.18)",
  },
  approach: {
    title: "The Approach",
    icon: Workflow,
    accent: "from-sky-500/[0.10] via-transparent to-cyan-500/[0.06]",
    dot: "bg-sky-400",
    glow: "rgba(56,189,248,0.18)",
  },
  outcome: {
    title: "The Outcome",
    icon: Sparkles,
    accent:
      "from-emerald-500/[0.10] via-transparent to-teal-500/[0.06]",
    dot: "bg-emerald-400",
    glow: "rgba(52,211,153,0.18)",
  },
  stack: {
    title: "Tech Stack",
    icon: Layers,
    accent:
      "from-violet-500/[0.10] via-transparent to-fuchsia-500/[0.06]",
    dot: "bg-violet-400",
    glow: "rgba(167,139,250,0.18)",
  },
};

export function buildSlides(project) {
  const slides = [
    { key: "cover", kind: "cover" },
    { key: "problem", kind: "problem", body: project.problem },
    { key: "approach", kind: "approach", body: project.approach },
    {
      key: "outcome",
      kind: "outcome",
      body: project.outcome,
      metrics: project.metrics,
    },
    { key: "stack", kind: "stack", stack: project.stack },
  ];
  if (project.video) {
    slides.push({ key: "demo", kind: "demo" });
  }
  return slides;
}

export function getSlideCount(project) {
  return 5 + (project.video ? 1 : 0);
}

export default function ProjectCarousel({
  project,
  resetOnExit = true,
}) {
  const slides = buildSlides(project);
  const rootRef = useRef(null);
  const wasVisibleRef = useRef(true);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    duration: 28,
    containScroll: false,
  });
  const [index, setIndex] = useState(0);

  const scrollTo = useCallback(
    (i) => emblaApi && emblaApi.scrollTo(i),
    [emblaApi]
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );
  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi) return undefined;
    const onSelect = () => setIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !rootRef.current || !resetOnExit) return undefined;
    const node = rootRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio > 0.4;
        // Reset to the cover slide whenever the section scrolls fully out of view,
        // so the user always arrives on slide 1 next time.
        if (!visible && wasVisibleRef.current) {
          emblaApi.scrollTo(0, true);
        }
        wasVisibleRef.current = visible;
      },
      { threshold: [0, 0.4, 0.6, 1] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [emblaApi, resetOnExit]);

  return (
    <div
      ref={rootRef}
      className="group relative h-full w-full select-none"
      data-testid={`project-${project.id}-carousel`}
    >
      <div className="h-full w-full overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, i) => (
            <div
              key={slide.key}
              className="relative h-full min-w-0 flex-[0_0_100%]"
              aria-hidden={index !== i}
            >
              <Slide slide={slide} project={project} active={index === i} />
            </div>
          ))}
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-0 z-30 flex items-center justify-between px-6 pt-6">
        <span className="font-display text-xs tracking-[0.3em] uppercase text-white/85 [text-shadow:0_1px_8px_rgba(0,0,0,0.6)]">
          Case {project.index}
        </span>
        {project.confidential ? (
          <span className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-black/55 backdrop-blur-md border border-white/10 text-[10px] font-body tracking-[0.18em] uppercase text-white/90">
            <Lock className="w-3 h-3" /> Enterprise
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-white/95 text-black text-[10px] font-body tracking-[0.18em] uppercase">
            Open Source
          </span>
        )}
      </div>

      <button
        type="button"
        aria-label="Previous slide"
        onClick={scrollPrev}
        className={`absolute left-3 top-1/2 z-30 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-black/40 text-white backdrop-blur-md transition-all duration-300 hover:bg-black/60 hover:opacity-100 focus-visible:opacity-100 md:left-4 md:opacity-0 md:pointer-events-auto md:group-hover:opacity-100 ${
          index === 0
            ? "opacity-70"
            : "opacity-0 pointer-events-none"
        }`}
        data-testid={`project-${project.id}-carousel-prev`}
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Next slide"
        onClick={scrollNext}
        className={`absolute right-3 top-1/2 z-30 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-black/40 text-white backdrop-blur-md transition-all duration-300 hover:bg-black/60 hover:opacity-100 focus-visible:opacity-100 md:right-4 md:opacity-0 md:pointer-events-auto md:group-hover:opacity-100 ${
          index === 0
            ? "opacity-70"
            : "opacity-0 pointer-events-none"
        }`}
        data-testid={`project-${project.id}-carousel-next`}
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex items-end justify-between px-6 pb-5">
        <div className="pointer-events-auto flex items-center gap-1.5">
          {slides.map((s, i) => (
            <button
              key={s.key}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollTo(i)}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                index === i
                  ? "w-8 bg-white"
                  : "w-1.5 bg-white/35 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
        <span className="font-body text-[10px] tracking-[0.3em] uppercase text-white/65">
          {String(index + 1).padStart(2, "0")} /{" "}
          {String(slides.length).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}

function Slide({ slide, project, active }) {
  if (slide.kind === "cover") return <CoverSlide project={project} active={active} />;
  if (slide.kind === "demo") return <DemoSlide project={project} active={active} />;
  if (slide.kind === "outcome")
    return <OutcomeSlide slide={slide} active={active} />;
  if (slide.kind === "stack")
    return <StackSlide slide={slide} active={active} />;
  return <NarrativeSlide slide={slide} active={active} />;
}

function CoverSlide({ project, active }) {
  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      <motion.img
        src={project.image}
        alt={project.name}
        draggable={false}
        initial={false}
        animate={
          active
            ? { scale: 1.02, x: 0, y: 0 }
            : { scale: 1.12, x: -6, y: -6 }
        }
        transition={{ duration: 8, ease }}
        className="absolute inset-0 h-full w-full object-cover grayscale-[8%]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-transparent to-transparent" />

      <div className="absolute inset-0 z-10 flex items-end px-6 pt-6 pb-16 md:px-10 md:pt-10 md:pb-20">
        <div className="max-w-md">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="font-body text-[10px] tracking-[0.4em] uppercase text-white/70"
          >
            Project {project.index}
          </motion.p>
          <motion.h3
            initial={{ opacity: 0, y: 24 }}
            animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.9, ease, delay: 0.2 }}
            className="mt-3 font-display text-2xl md:text-4xl tracking-[-0.025em] leading-[1.05] text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.55)]"
          >
            {project.name}
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
            transition={{ duration: 0.9, ease, delay: 0.3 }}
            className="mt-3 font-display text-base md:text-lg text-white/80 leading-snug"
          >
            {project.subtitle}
          </motion.p>
        </div>
      </div>
    </div>
  );
}

function SlideShell({ kind, children, active }) {
  const theme = SLIDE_THEMES[kind];
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0a0a0b]">
      <div
        className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${theme.accent}`}
      />
      <motion.div
        aria-hidden
        initial={false}
        animate={active ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.96 }}
        transition={{ duration: 1.2, ease }}
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: theme.glow }}
      />
      <motion.div
        aria-hidden
        initial={false}
        animate={active ? { opacity: 0.6, scale: 1 } : { opacity: 0, scale: 0.96 }}
        transition={{ duration: 1.4, ease, delay: 0.15 }}
        className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: theme.glow }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(circle at 22% 28%, rgba(255,255,255,0.06) 0%, transparent 55%), radial-gradient(circle at 78% 72%, rgba(255,255,255,0.05) 0%, transparent 55%)",
        }}
      />
      <div className="absolute inset-0 ring-1 ring-inset ring-white/5" />
      <div className="relative z-10 flex h-full items-center px-8 md:px-12">
        {children}
      </div>
    </div>
  );
}

function SlideHeader({ kind, active }) {
  const theme = SLIDE_THEMES[kind];
  const Icon = theme.icon;
  return (
    <div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
        transition={{ duration: 0.7, ease, delay: 0.1 }}
        className="flex items-center gap-2"
      >
        <span className={`h-1.5 w-1.5 rounded-full ${theme.dot}`} />
        <span className="font-body text-[10px] tracking-[0.4em] uppercase text-white/65">
          {theme.title}
        </span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, rotate: -8 }}
        animate={
          active
            ? { opacity: 1, scale: 1, rotate: 0 }
            : { opacity: 0, scale: 0.9, rotate: -8 }
        }
        transition={{ duration: 0.8, ease, delay: 0.2 }}
        className="mt-5"
      >
        <Icon className="h-10 w-10 text-white/85" strokeWidth={1.4} />
      </motion.div>
    </div>
  );
}

function NarrativeSlide({ slide, active }) {
  return (
    <SlideShell kind={slide.kind} active={active}>
      <div className="max-w-xl">
        <SlideHeader kind={slide.kind} active={active} />
        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
          transition={{ duration: 0.85, ease, delay: 0.32 }}
          className="mt-6 font-display text-lg md:text-2xl text-white leading-snug tracking-[-0.01em]"
        >
          {slide.body}
        </motion.p>
      </div>
    </SlideShell>
  );
}

function OutcomeSlide({ slide, active }) {
  return (
    <SlideShell kind={slide.kind} active={active}>
      <div className="w-full max-w-xl">
        <SlideHeader kind={slide.kind} active={active} />
        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
          transition={{ duration: 0.85, ease, delay: 0.32 }}
          className="mt-6 font-display text-base md:text-xl text-white leading-snug"
        >
          {slide.body}
        </motion.p>
        <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {slide.metrics.map((m, i) => (
            <motion.div
              key={`${m.l}-${i}`}
              initial={{ opacity: 0, y: 18 }}
              animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
              transition={{ duration: 0.65, ease, delay: 0.5 + i * 0.1 }}
              className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 backdrop-blur-sm"
            >
              <div className="font-display text-xl md:text-2xl text-white tracking-tight">
                {m.v}
              </div>
              <div className="mt-1 font-body text-[11px] text-white/65">
                {m.l}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

function StackSlide({ slide, active }) {
  return (
    <SlideShell kind={slide.kind} active={active}>
      <div className="w-full max-w-xl">
        <SlideHeader kind={slide.kind} active={active} />
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
          className="mt-6 font-display text-base md:text-lg text-white/85 leading-snug"
        >
          Built with the tools that earn their keep.
        </motion.p>
        <div className="mt-6 flex flex-wrap gap-2">
          {slide.stack.map((s, i) => (
            <motion.span
              key={s}
              initial={{ opacity: 0, y: 12, scale: 0.92 }}
              animate={
                active
                  ? { opacity: 1, y: 0, scale: 1 }
                  : { opacity: 0, y: 12, scale: 0.92 }
              }
              transition={{ duration: 0.55, ease, delay: 0.45 + i * 0.06 }}
              className="inline-flex h-9 items-center rounded-full border border-white/15 bg-white/[0.06] px-3.5 font-body text-xs tracking-wide text-white/90 backdrop-blur-sm"
            >
              {s}
            </motion.span>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

function DemoSlide({ project, active }) {
  const isEmbed = project.video && project.video.startsWith("http");
  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      <motion.div
        initial={false}
        animate={active ? { opacity: 1 } : { opacity: 0.55 }}
        transition={{ duration: 0.6, ease }}
        className="absolute inset-0"
      >
        {isEmbed ? (
          <iframe
            src={project.video}
            title={`${project.name} demo`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
            data-testid={`project-${project.id}-video`}
          />
        ) : (
          <video
            src={project.video}
            poster={project.image}
            controls
            playsInline
            preload="metadata"
            className="absolute inset-0 h-full w-full object-cover"
            data-testid={`project-${project.id}-video`}
          />
        )}
      </motion.div>
      <div className="pointer-events-none absolute left-6 top-16 z-20 flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-white/90 text-black">
          <Play className="h-3 w-3" fill="currentColor" />
        </span>
        <span className="font-body text-[10px] tracking-[0.4em] uppercase text-white/80 [text-shadow:0_1px_8px_rgba(0,0,0,0.6)]">
          Live Demo
        </span>
      </div>
    </div>
  );
}
